import { apiService } from '@shared/services/apiServices';
import { AxiosError } from 'axios';
import { CERTIFICATE_ERROR_MESSAGES } from '../errors/errorMessages';
import type { UpdateCertificateTemplatePayload } from '../types';
import certificateTemplateEndpoints from './certificateTemplateEndpoints';

function extractErrorMessage(errorData: unknown, status?: number): string {
  if (status === 401 || status === 403) {
    return CERTIFICATE_ERROR_MESSAGES.UNAUTHORIZED;
  }
  if (status === 500) {
    return CERTIFICATE_ERROR_MESSAGES.INTERNAL_SERVER_ERROR;
  }
  if (status === 400) {
    if (errorData && typeof errorData === 'object' && 'message' in errorData) {
      return (errorData as { message: string }).message;
    }
    return CERTIFICATE_ERROR_MESSAGES.VALIDATION_ERROR;
  }
  if (errorData && typeof errorData === 'object' && 'message' in errorData) {
    return (errorData as { message: string }).message;
  }
  return CERTIFICATE_ERROR_MESSAGES.DEFAULT_ERROR;
}

export interface UpdateCertificateTemplateResult {
  success: boolean;
  error?: string;
  notFound?: boolean;
}

/**
 * Updates an existing certificate template via PATCH.
 *
 * Endpoint: `certificate-template-management/{id}`. The `form_template_id`
 * is not part of the payload because the task form cannot be changed after
 * the template has been created.
 */
export async function updateCertificateTemplate(
  id: string,
  payload: UpdateCertificateTemplatePayload
): Promise<UpdateCertificateTemplateResult> {
  try {
    const result = await apiService.patchAPI(
      `${certificateTemplateEndpoints.LIST}/${id}`,
      payload,
      { skipGlobalErrorRedirect: true }
    );

    if (!result.error) {
      return { success: true };
    }

    if (result.status === 404) {
      return {
        success: false,
        notFound: true,
        error: CERTIFICATE_ERROR_MESSAGES.DEFAULT_ERROR,
      };
    }

    if (result.isNetworkError) {
      return {
        success: false,
        error: CERTIFICATE_ERROR_MESSAGES.NETWORK_ERROR,
      };
    }

    return {
      success: false,
      error: extractErrorMessage(result.data, result.status),
    };
  } catch (error) {
    if (error instanceof AxiosError) {
      const isNetworkError = !error.response && !!error.request;
      if (isNetworkError) {
        return {
          success: false,
          error: CERTIFICATE_ERROR_MESSAGES.NETWORK_ERROR,
        };
      }
      return {
        success: false,
        error: extractErrorMessage(
          error.response?.data,
          error.response?.status
        ),
      };
    }
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : CERTIFICATE_ERROR_MESSAGES.UNEXPECTED_ERROR,
    };
  }
}
